import { z } from 'zod'
import { BooleanWarehousePolicyType, CARRIERS, Carrier, WarehousePolicy, WarehousePolicyType, WarehouseView } from '@/console/api/types'

/**
 * LOGISPRD-15602 — 창고 정책 카드 3장(운영 플래그 · 택배사 · 출고번호 접두)의 순수 로직
 * (서버 값 → 폼 초기값, zod 검증, 폼 → 전송 payload, dirty 판정).
 * 카드 컴포넌트는 이 모듈이 만든 값·오류만 그린다. 접두 형식·중복 같은 서버 규칙은 FE 가 복제하지 않는다(BE 메시지를 그대로 보여준다).
 */

/** 운영 플래그 카드의 행 순서이자 저장 전송 순서(고정). */
export const BOOLEAN_POLICY_TYPES: readonly BooleanWarehousePolicyType[] = ['JA_PICKUP', 'WCS_SORTER', 'B2B_CUTOFF', 'CAPACITY_MANAGED']

export const BOOLEAN_POLICY_LABELS: Record<BooleanWarehousePolicyType, string> = {
  JA_PICKUP: 'JA 집하',
  WCS_SORTER: 'WCS 소터',
  B2B_CUTOFF: 'B2B 컷오프',
  CAPACITY_MANAGED: '캐파 관리',
}

/** 정책 type → 화면 라벨. 누락 칩(`missingAttributes`)도 이 라벨을 쓴다. */
export const POLICY_LABELS: Record<WarehousePolicyType, string> = {
  ...BOOLEAN_POLICY_LABELS,
  PARCEL_CARRIER: '택배사',
  OUTBOUND_SEQUENCE_PREFIX: '출고번호 접두',
}

/** 셀렉트의 "선택 안 함" 값. 전송 시 키를 생략한다. */
export const NO_CARRIER = '' as const
export type CarrierChoice = Carrier | typeof NO_CARRIER

export type BooleanPolicyForm = { enabled: boolean }
/** `key` 는 행 추가/삭제 시 React key 용이다. 검증·전송에는 쓰지 않는다. */
export type ShipperRowForm = { key: string; code: string; carrier: CarrierChoice }
export type ParcelCarrierForm = { carrier: CarrierChoice; shippers: ShipperRowForm[] }
export type OutboundPrefixForm = { prefix: string }

/** 서버 응답에서 type 하나를 찾는다. 같은 type 이 여러 개일 일은 없지만 첫 항목을 쓴다. */
export const findPolicy = <T extends WarehousePolicy['type']>(view: WarehouseView, type: T): Extract<WarehousePolicy, { type: T }> | undefined =>
  view.policies?.policies?.find((policy): policy is Extract<WarehousePolicy, { type: T }> => policy.type === type)

/* ───────── 서버 값 → 폼 초기값 ───────── */

// `enabled` 키가 빠졌거나 정책 자체가 없으면 BE 읽기 로직과 같이 false 다.
export const initialBooleanForm = (view: WarehouseView, type: BooleanWarehousePolicyType): BooleanPolicyForm => ({
  enabled: findPolicy(view, type)?.enabled === true,
})

/**
 * 서버에 그 type 항목이 있는지. 없으면 `미설정` — 활성화 완비 조건을 채우려면 명시 저장해야 한다.
 * 불리언 4종은 BE 완비 판정(`WarehouseAttributeCompleteness`, `enabled != null`)과 맞춰 `enabled` 가 null 이거나 키가 없어도 미설정이다.
 */
export const isConfigured = (view: WarehouseView, type: WarehousePolicyType): boolean => {
  const policy = findPolicy(view, type)
  if (policy === undefined) {
    return false
  }
  return (BOOLEAN_POLICY_TYPES as readonly WarehousePolicyType[]).includes(type) ? typeof (policy as { enabled?: boolean | null }).enabled === 'boolean' : true
}

/* ───────── 운영 플래그(불리언 4종) ───────── */

/** 사용자가 손댄 행만 담는다. 손대지 않은 행은 서버 값을 그대로 보인다. */
export type FlagEdits = Partial<Record<BooleanWarehousePolicyType, boolean>>

export type FlagRow = {
  type: BooleanWarehousePolicyType
  label: string
  /**
   * 화면 값 — 편집값이 있으면 편집값, 없으면 서버 값(`enabled` 키 누락 = false).
   * 미설정 행은 사용자가 고르기 전까지 `null` 이다. 기본 OFF 를 보이면 저장 한 번에 false 가 기록되기 때문이다.
   */
  enabled: boolean | null
  configured: boolean
  /** 서버 값과 다르게 편집됨. 미설정 행은 서버 값이 없어 이 값이 false 다(대신 `pending`). */
  changed: boolean
  /** 저장 대상 — 바뀌었거나, 미설정 행에서 값을 골랐다. */
  pending: boolean
}

export const flagRows = (view: WarehouseView, edits: FlagEdits): FlagRow[] =>
  BOOLEAN_POLICY_TYPES.map((type) => {
    const server = initialBooleanForm(view, type).enabled
    const configured = isConfigured(view, type)
    const edited = edits[type]
    const enabled = edited ?? (configured ? server : null)
    const changed = configured && edited !== undefined && edited !== server
    return { type, label: BOOLEAN_POLICY_LABELS[type], enabled, configured, changed, pending: changed || (!configured && edited !== undefined) }
  })

/** 미설정인데 아직 값을 고르지 않은 행 수. 0 이 아니면 저장을 막는다. */
export const unchosenFlagCount = (rows: readonly FlagRow[]): number => rows.filter((row) => row.enabled === null).length

/** 저장 전송값 — 저장 대상 행만, 고정 순서로. */
export const pendingFlagPolicies = (rows: readonly FlagRow[]): WarehousePolicy[] =>
  rows.filter((row) => row.pending).map((row) => toBooleanPolicy(row.type, { enabled: row.enabled === true }))

let rowSeq = 0
export const newShipperRow = (code = '', carrier: CarrierChoice = NO_CARRIER): ShipperRowForm => {
  rowSeq += 1
  return { key: `row-${rowSeq}`, code, carrier }
}

export const initialParcelCarrierForm = (view: WarehouseView): ParcelCarrierForm => {
  const policy = findPolicy(view, 'PARCEL_CARRIER')
  return {
    carrier: policy?.carrier ?? NO_CARRIER,
    shippers: (policy?.shippers ?? []).map((row) => newShipperRow(row.code, row.carrier)),
  }
}

/** 서버에 저장된 접두. 없으면 null — 값이 있으면 카드가 입력을 잠근다. */
export const savedPrefix = (view: WarehouseView): string | null => {
  const prefix = findPolicy(view, 'OUTBOUND_SEQUENCE_PREFIX')?.prefix
  return prefix && prefix.length > 0 ? prefix : null
}

/* ───────── zod 스키마 ───────── */

const carrierField = z.enum(CARRIERS, { errorMap: () => ({ message: '택배사를 선택하세요.' }) })

const shipperRowSchema = z.object({
  code: z.string().trim().min(1, '화주코드를 입력하세요.'),
  carrier: carrierField,
})

export const parcelCarrierFormSchema = z
  .object({
    carrier: z.union([carrierField, z.literal(NO_CARRIER)]),
    shippers: z.array(shipperRowSchema),
  })
  .superRefine((form, ctx) => {
    if (form.carrier === NO_CARRIER && form.shippers.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '기본 택배사나 화주별 택배사를 하나 이상 입력하세요.' })
    }

    // 처음 나온 행 이후의 같은 code 행마다 오류를 단다 — 셋 이상 겹칠 때 뒤 행이 통과처럼 보이지 않게.
    const seen = new Set<string>()
    form.shippers.forEach((row, index) => {
      const code = row.code.trim()
      if (code.length === 0) {
        return
      }
      if (seen.has(code)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['shippers', index, 'code'], message: `화주코드가 중복됐어요: ${code}` })
      }
      seen.add(code)
    })
  })

export const outboundPrefixFormSchema = z.object({
  prefix: z
    .string()
    .trim()
    .min(1, '접두를 입력하세요.')
    .refine((value) => !/\s/.test(value), '접두에는 공백을 넣을 수 없어요.'),
})

/** 검증 오류 메시지 목록(중복 제거, 순서 유지). 통과하면 빈 배열. */
export const validationMessages = (schema: z.ZodTypeAny, value: unknown): string[] => {
  const result = schema.safeParse(value)
  if (result.success) {
    return []
  }
  return [...new Set(result.error.issues.map((issue) => issue.message))]
}

/* ───────── 폼 → 전송 payload ───────── */

export const toBooleanPolicy = (type: BooleanWarehousePolicyType, form: BooleanPolicyForm): WarehousePolicy => ({ type, enabled: form.enabled })

/** carrier 미선택이면 키를 넣지 않고, shippers 가 0행이면 키를 넣지 않는다. code 는 trim 한다. */
export const toParcelCarrierPolicy = (form: ParcelCarrierForm): WarehousePolicy => {
  const policy: Extract<WarehousePolicy, { type: 'PARCEL_CARRIER' }> = { type: 'PARCEL_CARRIER' }
  if (form.carrier !== NO_CARRIER) {
    policy.carrier = form.carrier
  }
  if (form.shippers.length > 0) {
    // 저장은 parcelCarrierIssues 가 비어야 열리고, 행 스키마(carrierField = z.enum(CARRIERS))가 미선택('')을 거부한다 — 여기 오는 행의 carrier 는 Carrier 다.
    policy.shippers = form.shippers.map((row) => ({ code: row.code.trim(), carrier: row.carrier as Carrier }))
  }
  return policy
}

export const toOutboundPrefixPolicy = (form: OutboundPrefixForm): WarehousePolicy => ({
  type: 'OUTBOUND_SEQUENCE_PREFIX',
  prefix: form.prefix.trim(),
})

/* ───────── PARCEL_CARRIER 오류 배치 · dirty ───────── */

export type ParcelCarrierIssues = {
  /** 행에 속하지 않는 오류(예: 전부 비었음). */
  form: string[]
  /** `rows[i]` = i 번째 화주 행 아래에 보일 오류. */
  rows: string[][]
}

/** zod 오류를 행 아래 / 카드 하단으로 나눈다. 같은 자리의 같은 문구는 한 번만. */
export const parcelCarrierIssues = (form: ParcelCarrierForm): ParcelCarrierIssues => {
  const issues: ParcelCarrierIssues = { form: [], rows: form.shippers.map(() => []) }
  const result = parcelCarrierFormSchema.safeParse(form)
  if (result.success) {
    return issues
  }
  result.error.issues.forEach((issue) => {
    const [head, index] = issue.path
    const bucket = head === 'shippers' && typeof index === 'number' && issues.rows[index] ? issues.rows[index] : issues.form
    if (!bucket.includes(issue.message)) {
      bucket.push(issue.message)
    }
  })
  return issues
}

export const hasParcelCarrierIssues = (issues: ParcelCarrierIssues): boolean => issues.form.length > 0 || issues.rows.some((row) => row.length > 0)

/** 비교용 정규형 — 전송값과 같은 규칙(키 생략 · trim)으로 접는다. 서버 값과 같으면 저장할 이유가 없다. */
const normalizeParcel = (carrier: CarrierChoice | null | undefined, shippers: readonly { code: string; carrier: CarrierChoice }[]): string =>
  JSON.stringify({ carrier: carrier || null, shippers: shippers.map((row) => [row.code.trim(), row.carrier]) })

export const isParcelCarrierDirty = (view: WarehouseView, form: ParcelCarrierForm): boolean => {
  const server = findPolicy(view, 'PARCEL_CARRIER')
  return normalizeParcel(server?.carrier, server?.shippers ?? []) !== normalizeParcel(form.carrier, form.shippers)
}
